import { Dispatch } from 'redux'

import { useThunkActions } from 'redux/actions/useThunkActions'
import { create, destroy, getAll, update } from 'redux/api/Tasks'

import * as actions from './actions'
import type {
  TCreateTaskRequestBody,
  TUpdateTaskRequestBody
} from '@homepal/api-contract'
import { toggleSnackbarOpen } from 'redux/actions/Snackbar'
import { SnackbarMessage } from 'types/GlobalSnackbar'
import { EReduxState } from 'redux/utils/State'

import { ApplicationState } from 'redux/Stores/types'

/** Fetches the tasks unless they are loading or loaded. */
export function tryGetAllTasks() {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    const { state } = getState().TaskStore

    if (state === EReduxState.LOADING || state === EReduxState.SUCCESS) {
      return
    }

    tryRefetchAllTasks()(dispatch)
  }
}

/** Fetches the tasks even if they are loaded, after the server changed them. */
export function tryRefetchAllTasks() {
  return (dispatch: Dispatch) => {
    dispatch(actions.getAll())

    getAll()
      .then((response) => {
        dispatch(actions.getAllSuccess(response.data))
      })
      .catch(() => {
        dispatch(actions.getAllFailed())
      })
  }
}

function tryCreateTask(
  body: TCreateTaskRequestBody,
  cb?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.create())

    create({ data: body })
      .then((response) => {
        dispatch(actions.createSuccess(response.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.TaskCreated))
        if (cb) {
          cb()
        }
      })
      .catch(() => {
        dispatch(actions.createFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.TaskCreatedFailed))

        if (cb) {
          cb(true)
        }
      })
  }
}

function tryUpdateTask(
  id: string,
  body: TUpdateTaskRequestBody,
  cb?: (err?: true) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.update())

    update(id, { data: body })
      .then((response) => {
        dispatch(actions.updateSuccess(response.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.TaskUpdated))
        if (cb) {
          cb()
        }
      })
      .catch(() => {
        dispatch(actions.updateFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.TaskUpdatedFailed))
        if (cb) {
          cb(true)
        }
      })
  }
}

function tryDeleteTask(id: string, cb?: (err?: true) => void) {
  return (dispatch: Dispatch) => {
    dispatch(actions.destroy())

    destroy(id)
      .then(() => {
        dispatch(actions.destroySuccess(id))

        dispatch(toggleSnackbarOpen(SnackbarMessage.TaskDeleted))

        if (cb) {
          cb()
        }
      })
      .catch(() => {
        dispatch(actions.destroyFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.TaskDeletedFailed))

        if (cb) {
          cb(true)
        }
      })
  }
}

export const useTaskActions = () => {
  return {
    tryGetAllTasks: useThunkActions(tryGetAllTasks),
    tryCreateTask: useThunkActions(tryCreateTask),
    tryUpdateTask: useThunkActions(tryUpdateTask),
    tryDeleteTask: useThunkActions(tryDeleteTask)
  }
}
